import { useState, useEffect, FormEvent } from 'react';
import { Link as RouterLink, useParams, useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';
import Link from '@mui/material/Link';
import StorefrontIcon from '@mui/icons-material/Storefront';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { validateResetToken, resetPassword } from '../utils/auth';
import { palette, typography, theme } from '../theme/colors';

export const ResetPasswordPage = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(true);
  const [error, setError] = useState('');
  const [tokenValid, setTokenValid] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    const checkToken = async () => {
      if (!token) {
        setError('Invalid reset link');
        setValidating(false);
        return;
      }

      try {
        const result = await validateResetToken(token);
        if (result.valid) {
          setTokenValid(true);
        } else {
          setError('This reset link has expired or is invalid');
        }
      } catch {
        setError('This reset link has expired or is invalid');
      } finally {
        setValidating(false);
      }
    };

    checkToken();
  }, [token]);

  const validatePassword = (pwd: string): string | null => {
    if (pwd.length < 8) {
      return 'Password must be at least 8 characters';
    }
    if (!/[A-Z]/.test(pwd)) {
      return 'Password must contain at least one uppercase letter';
    }
    if (!/[a-z]/.test(pwd)) {
      return 'Password must contain at least one lowercase letter';
    }
    if (!/[0-9]/.test(pwd)) {
      return 'Password must contain at least one number';
    }
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(pwd)) {
      return 'Password must contain at least one special character';
    }
    return null;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');

    const passwordError = validatePassword(password);
    if (passwordError) {
      setError(passwordError);
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);

    try {
      await resetPassword(token!, password);
      setSuccess(true);
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to reset password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Loading state while validating token
  if (validating) {
    return (
      <Box
        sx={{
          display: 'flex',
          minHeight: '100vh',
          justifyContent: 'center',
          alignItems: 'center',
          backgroundColor: palette.background,
        }}
      >
        <CircularProgress sx={{ color: palette.primary }} />
      </Box>
    );
  }

  // Invalid token state
  if (!tokenValid && !success) {
    return (
      <Box
        sx={{
          display: 'flex',
          minHeight: '100vh',
          justifyContent: 'center',
          alignItems: 'center',
          backgroundColor: palette.background,
          p: { xs: 3, sm: 6 },
        }}
      >
        <Box sx={{ width: '100%', maxWidth: 400, textAlign: 'center' }}>
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: theme.radius.xl,
              background: `linear-gradient(135deg, ${palette.error} 0%, #dc2626 100%)`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
              mb: 3,
            }}
          >
            <ErrorOutlineIcon sx={{ color: '#fff', fontSize: 32 }} />
          </Box>

          <Typography
            variant="h4"
            sx={{
              fontFamily: typography.fontFamily.display,
              fontWeight: typography.fontWeight.bold,
              color: palette.textPrimary,
              mb: 2,
            }}
          >
            Invalid Link
          </Typography>

          <Alert
            severity="error"
            sx={{
              mb: 3,
              borderRadius: theme.radius.lg,
              backgroundColor: palette.errorMuted,
              color: palette.error,
              border: `1px solid ${palette.error}30`,
              '& .MuiAlert-icon': {
                color: palette.error,
              },
            }}
          >
            {error}
          </Alert>

          <Typography
            variant="body2"
            sx={{ color: palette.textSecondary, mb: 4 }}
          >
            Please request a new password reset link.
          </Typography>

          <Link
            component={RouterLink}
            to="/forgot-password"
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 1,
              color: palette.primary,
              textDecoration: 'none',
              fontWeight: typography.fontWeight.medium,
              '&:hover': {
                textDecoration: 'underline',
              },
            }}
          >
            Request new link
            <ArrowForwardIcon sx={{ fontSize: 18 }} />
          </Link>
        </Box>
      </Box>
    );
  }

  // Success state
  if (success) {
    return (
      <Box
        sx={{
          display: 'flex',
          minHeight: '100vh',
          justifyContent: 'center',
          alignItems: 'center',
          backgroundColor: palette.background,
          p: { xs: 3, sm: 6 },
        }}
      >
        <Box sx={{ width: '100%', maxWidth: 400, textAlign: 'center' }}>
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: theme.radius.xl,
              background: `linear-gradient(135deg, ${palette.success} 0%, ${palette.successDark} 100%)`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
              mb: 3,
            }}
          >
            <CheckCircleOutlineIcon sx={{ color: '#fff', fontSize: 32 }} />
          </Box>

          <Typography
            variant="h4"
            sx={{
              fontFamily: typography.fontFamily.display,
              fontWeight: typography.fontWeight.bold,
              color: palette.textPrimary,
              mb: 2,
            }}
          >
            Password Reset!
          </Typography>

          <Alert
            severity="success"
            sx={{
              mb: 3,
              borderRadius: theme.radius.lg,
              backgroundColor: palette.successMuted || '#ecfdf5',
              color: palette.success,
              border: `1px solid ${palette.success}30`,
              '& .MuiAlert-icon': {
                color: palette.success,
              },
            }}
          >
            Your password has been successfully reset.
          </Alert>

          <Typography
            variant="body2"
            sx={{ color: palette.textSecondary, mb: 4 }}
          >
            Redirecting to login page in a few seconds...
          </Typography>

          <Link
            component={RouterLink}
            to="/login"
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 1,
              color: palette.primary,
              textDecoration: 'none',
              fontWeight: typography.fontWeight.medium,
              '&:hover': {
                textDecoration: 'underline',
              },
            }}
          >
            <ArrowBackIcon sx={{ fontSize: 18 }} />
            Go to login now
          </Link>
        </Box>
      </Box>
    );
  }

  // Reset password form
  return (
    <Box
      sx={{
        display: 'flex',
        minHeight: '100vh',
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: palette.background,
        position: 'relative',
        overflow: 'hidden',
        p: { xs: 3, sm: 6 },
      }}
    >
      {/* Background Gradient Mesh */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          background: theme.gradients.mesh,
          opacity: 0.5,
        }}
      />

      {/* Animated Gradient Orb */}
      <Box
        sx={{
          position: 'absolute',
          top: '20%',
          right: '20%',
          width: 300,
          height: 300,
          borderRadius: '50%',
          background: `radial-gradient(circle, ${palette.primary}20 0%, transparent 70%)`,
          filter: 'blur(60px)',
          animation: 'float 8s ease-in-out infinite',
          '@keyframes float': {
            '0%, 100%': { transform: 'translate(0, 0)' },
            '50%': { transform: 'translate(30px, -30px)' },
          },
        }}
      />

      <Box
        sx={{
          position: 'relative',
          zIndex: 1,
          width: '100%',
          maxWidth: 400,
          backgroundColor: palette.surface,
          borderRadius: theme.radius.xl,
          border: `1px solid ${palette.border}`,
          p: { xs: 3, sm: 4 },
        }}
      >
        {/* Logo */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            mb: 4,
          }}
        >
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: theme.radius.lg,
              background: theme.gradients.primary,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <StorefrontIcon sx={{ color: palette.textOnPrimary, fontSize: 24 }} />
          </Box>
          <Typography
            variant="h5"
            sx={{
              fontFamily: typography.fontFamily.display,
              fontWeight: typography.fontWeight.bold,
              color: palette.textPrimary,
            }}
          >
            Nexera
          </Typography>
        </Box>

        <Typography
          variant="h4"
          sx={{
            fontFamily: typography.fontFamily.display,
            fontWeight: typography.fontWeight.bold,
            color: palette.textPrimary,
            mb: 1,
          }}
        >
          Set New Password
        </Typography>

        <Typography
          variant="body2"
          sx={{ color: palette.textSecondary, mb: 4 }}
        >
          Enter your new password below. Make sure it's strong and secure.
        </Typography>

        {error && (
          <Alert
            severity="error"
            sx={{
              mb: 3,
              borderRadius: theme.radius.lg,
              backgroundColor: palette.errorMuted,
              color: palette.error,
              border: `1px solid ${palette.error}30`,
              '& .MuiAlert-icon': {
                color: palette.error,
              },
            }}
          >
            {error}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <Box sx={{ mb: 3 }}>
            <Typography
              variant="caption"
              sx={{
                color: palette.textSecondary,
                fontWeight: typography.fontWeight.medium,
                mb: 1,
                display: 'block',
              }}
            >
              New Password
            </Typography>
            <TextField
              fullWidth
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              placeholder="Enter new password"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockOutlinedIcon sx={{ color: palette.textMuted, fontSize: 20 }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      sx={{ color: palette.textMuted }}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: palette.surfaceElevated,
                  borderRadius: theme.radius.lg,
                  '& fieldset': {
                    borderColor: palette.border,
                  },
                  '&:hover fieldset': {
                    borderColor: palette.borderLight,
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: palette.primary,
                  },
                },
                '& .MuiInputBase-input': {
                  color: palette.textPrimary,
                  '&::placeholder': {
                    color: palette.textMuted,
                    opacity: 1,
                  },
                },
              }}
            />
            <Typography
              variant="caption"
              sx={{
                color: palette.textMuted,
                mt: 1,
                display: 'block',
              }}
            >
              Min 8 chars with uppercase, lowercase, number & special character
            </Typography>
          </Box>

          <Box sx={{ mb: 3 }}>
            <Typography
              variant="caption"
              sx={{
                color: palette.textSecondary,
                fontWeight: typography.fontWeight.medium,
                mb: 1,
                display: 'block',
              }}
            >
              Confirm Password
            </Typography>
            <TextField
              fullWidth
              type={showConfirmPassword ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              placeholder="Confirm new password"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockOutlinedIcon sx={{ color: palette.textMuted, fontSize: 20 }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      edge="end"
                      sx={{ color: palette.textMuted }}
                    >
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: palette.surfaceElevated,
                  borderRadius: theme.radius.lg,
                  '& fieldset': {
                    borderColor: palette.border,
                  },
                  '&:hover fieldset': {
                    borderColor: palette.borderLight,
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: palette.primary,
                  },
                },
                '& .MuiInputBase-input': {
                  color: palette.textPrimary,
                  '&::placeholder': {
                    color: palette.textMuted,
                    opacity: 1,
                  },
                },
              }}
            />
          </Box>

          <Button
            fullWidth
            type="submit"
            variant="contained"
            size="large"
            disabled={loading}
            endIcon={!loading && <ArrowForwardIcon />}
            sx={{
              py: 1.75,
              background: theme.gradients.primary,
              color: palette.textOnPrimary,
              fontWeight: typography.fontWeight.semibold,
              fontSize: typography.fontSize.md,
              borderRadius: theme.radius.lg,
              mb: 3,
              '&:hover': {
                transform: 'translateY(-2px)',
              },
              '&:disabled': {
                background: palette.border,
                color: palette.textMuted,
              },
            }}
          >
            {loading ? (
              <CircularProgress size={24} sx={{ color: palette.textOnPrimary }} />
            ) : (
              'Reset Password'
            )}
          </Button>
        </form>

        <Box sx={{ textAlign: 'center' }}>
          <Link
            component={RouterLink}
            to="/login"
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 1,
              color: palette.primary,
              textDecoration: 'none',
              fontWeight: typography.fontWeight.medium,
              '&:hover': {
                textDecoration: 'underline',
              },
            }}
          >
            <ArrowBackIcon sx={{ fontSize: 18 }} />
            Back to login
          </Link>
        </Box>
      </Box>
    </Box>
  );
};
